import React, { useEffect, useState } from 'react';
import { BaseDialog } from '../../base/dialog';
import { JsonDiffReact } from '../../json-diff';
import { CoreFetch } from '../../../api';
import { CodingApi } from '../../../api/server';
import { ProviderCoding, SupportCoding } from '../../../api/server/models';
import styled from '@emotion/styled';
import { Variables } from '../../base/variables';

interface ICodingDetailModalProps {
    codingId: string;
    isSupportApp: boolean;
    open: boolean;
    onClose: () => void;
}

// Styled components similar to claim-timeline-table styles
const ExpandWrap = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    flex-grow: 1;
`;

const ExpandCol = styled.div`
    width: 100%;
`;

const ExpandTable = styled.table`
    white-space: normal;
    color: ${Variables.Colors.n100};
    margin: 0;
    width: 100%;
`;

const ExpandTD = styled.td<{ colSpan?: number }>`
    text-align: left;
    font-size: 16px;
    letter-spacing: 0.5px;
    padding: 4px 8px;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ExpandTH = styled.th`
    text-align: left;
    vertical-align: top;
    font-weight: 500;
    font-size: 16px;
    letter-spacing: 0.5px;
    padding: 4px 8px;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ExpandTR = styled.tr``;
const ExpandTBody = styled.tbody``;

const LoadingMessage = styled.div`
    padding: 20px;
    text-align: center;
    color: ${Variables.Colors.n70};
`;

const ErrorMessage = styled.div`
    padding: 20px;
    text-align: center;
    color: ${Variables.Colors.r50};
`;

const ComparisonGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
`;

const ComparisonColumn = styled.div`
    min-width: 0;
`;

const ComparisonLabel = styled.div`
    font-weight: 500;
    margin-bottom: 8px;
`;

const ComparisonBody = styled.div`
    overflow: hidden;
`;

export const CodingDetailModal: React.FunctionComponent<ICodingDetailModalProps> = (
    props: ICodingDetailModalProps,
): React.ReactElement => {
    const { codingId, isSupportApp, open, onClose } = props;
    const [coding, setCoding] = useState<ProviderCoding | SupportCoding | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open && codingId) {
            fetchCodingDetails();
        }
    }, [open, codingId, isSupportApp]);

    const fetchCodingDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const codingApi = CoreFetch(CodingApi);
            let codingData: ProviderCoding | SupportCoding;

            if (isSupportApp) {
                codingData = await codingApi.getSupportCoding({ id: codingId });
            } else {
                codingData = await codingApi.getProviderCoding({ id: codingId });
            }

            setCoding(codingData);
        } catch (err) {
            console.error('Failed to fetch coding details:', err);
            setError('Failed to load coding details');
        } finally {
            setLoading(false);
        }
    };

    const renderContent = () => {
        if (loading) {
            return <LoadingMessage>Loading coding details...</LoadingMessage>;
        }

        if (error) {
            return <ErrorMessage>{error}</ErrorMessage>;
        }

        if (!coding) {
            return <LoadingMessage>No coding data available</LoadingMessage>;
        }

        let codingNotes = coding.notes;
        if (codingNotes && typeof codingNotes === 'string') {
            try {
                const parsed = JSON.parse(codingNotes);
                codingNotes = parsed;
            } catch (parseError) {
                console.error('Failed to parse coding notes as JSON:', parseError, codingNotes);
                codingNotes = 'Invalid JSON';
            }
        }

        return (
            <ExpandWrap key={coding.id}>
                <ExpandCol>
                    <ExpandTable>
                        <ExpandTBody>
                            <ExpandTR>
                                <ExpandTH>Coding ID</ExpandTH>
                                <ExpandTD>{coding.id}</ExpandTD>
                            </ExpandTR>
                            <ExpandTR>
                                <ExpandTH>Vendor</ExpandTH>
                                <ExpandTD>{coding.vendor}</ExpandTD>
                            </ExpandTR>
                            <ExpandTR>
                                <ExpandTH>Status</ExpandTH>
                                <ExpandTD>{coding.status}</ExpandTD>
                            </ExpandTR>
                            <ExpandTR>
                                <ExpandTH>Notes</ExpandTH>
                                <ExpandTD>
                                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {codingNotes
                                            ? typeof codingNotes === 'string'
                                                ? codingNotes
                                                : JSON.stringify(codingNotes, null, 2)
                                            : '-'}
                                    </pre>
                                </ExpandTD>
                            </ExpandTR>
                            <ExpandTR>
                                <ExpandTD colSpan={2}>
                                    <ComparisonGrid>
                                        <ComparisonColumn>
                                            <ComparisonLabel>Before</ComparisonLabel>
                                            <ComparisonBody>
                                                <pre style={{ fontSize: '12px' }}>
                                                    {coding.before ? JSON.stringify(coding.before, null, 4) : ''}
                                                </pre>
                                            </ComparisonBody>
                                        </ComparisonColumn>
                                        <ComparisonColumn>
                                            <ComparisonLabel>After</ComparisonLabel>
                                            <ComparisonBody>
                                                <pre style={{ fontSize: '12px' }}>
                                                    {coding.after ? JSON.stringify(coding.after, null, 4) : ''}
                                                </pre>
                                            </ComparisonBody>
                                        </ComparisonColumn>
                                        <ComparisonColumn style={{ gridColumn: '1 / -1' }}>
                                            <ComparisonLabel>Diff</ComparisonLabel>
                                            <ComparisonBody>
                                                <JsonDiffReact right={coding.after} left={coding.before} show={false} />
                                            </ComparisonBody>
                                        </ComparisonColumn>
                                    </ComparisonGrid>
                                </ExpandTD>
                            </ExpandTR>
                        </ExpandTBody>
                    </ExpandTable>
                </ExpandCol>
            </ExpandWrap>
        );
    };

    return (
        <BaseDialog
            open={open}
            onClose={onClose}
            renderTitle="Coding Details"
            showCloseButton={true}
            maxWidth="lg"
            fullWidth={true}
            expandable={true}
            style={{ zIndex: 10001 }}
            slotProps={{
                backdrop: {
                    style: { zIndex: 10001 },
                },
                paper: {
                    style: { zIndex: 10002 },
                },
            }}>
            {renderContent()}
        </BaseDialog>
    );
};
